import { useState } from "react";
import { Button, FAQAccordion, IgIcon, Seo } from "../../components";
import { MapPin, Phone, Mail, Clock, Send, Share2 } from "lucide-react";
import "./KontaktPage.scss";
import { CaptchaWidget } from "../../components/CaptchaWidget/CaptchaWidget";
import { CONTACT_URL } from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import axios from "axios";

function KontaktPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Prosím, potvrďte, že nie ste robot.");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const { data } = await axios.post(CONTACT_URL, {
        ...form,
        cfTurnstileToken: captchaToken,
      });

      if (data.success) {
        setStatus("success");
        setForm({ name: "", email: "", subject: "", message: "" });
        setCaptchaToken(null);
      } else {
        setStatus("error");
        setErrorMessage(
          data.message || "Odoslanie správy zlyhalo. Skúste to znova.",
        );
      }
    } catch (error) {
      setStatus("error");
      const msg = handleApiError(error, "Chyba pripojenia. Skúste to znova.");
      setErrorMessage(msg);
    }
  };

  return (
    <div className="kontakt">
      <Seo
        title="Kontakt"
        description="Ozvite sa nám! Kontaktujte školu Capoeira Bratislava cez formulár, e-mail alebo telefón. Téšíme sa na vás."
        canonical="/kontakt"
      />
      <section className="kontakt__hero">
        <div className="kontakt__hero-container">
          <span className="kontakt__hero-tag">Kontakt</span>
          <h1 className="kontakt__hero-title">Ozvite sa nám</h1>
        </div>
      </section>

      <section className="kontakt__main">
        <div className="kontakt__container">
          <div className="kontakt__grid">
            {/* Contact info */}
            <div className="kontakt__info">
              <h2 className="kontakt__info-title">Kontaktné údaje</h2>

              <div className="kontakt__info-items">
                <div className="kontakt__info-item">
                  <div className="kontakt__info-icon">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <strong>Adresa</strong>
                    <p>
                      Spojená škola Novohradská (GJH) Novohradská 3, 821 09
                      Bratislava
                    </p>
                  </div>
                </div>

                <div className="kontakt__info-item">
                  <div className="kontakt__info-icon">
                    <Phone size={20} />
                  </div>
                  <div>
                    <strong>Telefón</strong>
                    <p>+421 903 664 688</p>
                  </div>
                </div>

                <div className="kontakt__info-item">
                  <div className="kontakt__info-icon">
                    <Mail size={20} />
                  </div>
                  <div>
                    <strong>Email</strong>
                    <p>capoeira@capoeira.sk</p>
                  </div>
                </div>

                <div className="kontakt__info-item">
                  <div className="kontakt__info-icon">
                    <Share2 size={20} />
                  </div>
                  <div>
                    <strong>
                      <a
                        href="https://www.instagram.com/abada_capoeira_slovensko/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Instagram
                      </a>
                    </strong>
                    <a
                      href="https://www.facebook.com/AbadaCapoeiraSlovakia/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Facebook
                    </a>
                  </div>
                </div>

                <div className="kontakt__info-item">
                  <div className="kontakt__info-icon">
                    <Clock size={20} />
                  </div>
                  <div>
                    <strong>Tréningové hodiny</strong>
                    <p>Po: 16:00 – 17:00 a 17:00 – 18:00</p>
                    <p>St: 16:00 – 17:00 a 17:00 – 18:00</p>
                  </div>
                </div>
              </div>

              <div className="kontakt__map">
                <iframe
                  title="Mapa"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2661.5!2d17.1077!3d48.1486!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDA4JzU1LjAiTiAxN8KwMDYnMjcuNyJF!5e0!3m2!1ssk!2ssk!4v1"
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: "8px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact form */}
            <div className="kontakt__form-wrapper">
              <h2 className="kontakt__form-title">Napíšte nám</h2>

              {status === "success" ? (
                <div className="kontakt__success">
                  <div className="kontakt__success-icon">✓</div>
                  <h3>Správa odoslaná!</h3>
                  <p>Ďakujeme za vašu správu. Ozveme sa vám čo najskôr.</p>
                  <Button variant="ghost" onClick={() => setStatus("idle")}>
                    Odoslať ďalšiu správu
                  </Button>
                </div>
              ) : (
                <form className="kontakt__form" onSubmit={handleSubmit}>
                  <div className="kontakt__form-row">
                    <div className="kontakt__form-field">
                      <label className="kontakt__form-label">Meno</label>
                      <input
                        className="kontakt__form-input"
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Vaše meno"
                        required
                      />
                    </div>
                    <div className="kontakt__form-field">
                      <label className="kontakt__form-label">Email</label>
                      <input
                        type="email"
                        className="kontakt__form-input"
                        value={form.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="vas@email.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="kontakt__form-field">
                    <label className="kontakt__form-label">Predmet</label>
                    <input
                      className="kontakt__form-input"
                      value={form.subject}
                      onChange={(e) => handleChange("subject", e.target.value)}
                      placeholder="O čom chcete písať?"
                      required
                    />
                  </div>
                  <div className="kontakt__form-field">
                    <label className="kontakt__form-label">Správa</label>
                    <textarea
                      className="kontakt__form-textarea"
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Vaša správa..."
                      rows={5}
                      required
                    />
                  </div>
                  <CaptchaWidget onVerify={(token) => setCaptchaToken(token)} />
                  {status === "error" && (
                    <p className="kontakt__form-error">{errorMessage}</p>
                  )}
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={status === "sending"}
                  >
                    <Send size={16} />
                    {status === "sending" ? "Odosiela sa..." : "Odoslať správu"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion
        tag="CHCEM ZAČAŤ"
        title="Čo potrebuješ vedieť pred prvým tréningom:"
        items={[
          {
            question: "Prvý tréning je naozaj zdarma?",
            answer:
              "Áno. Prvý tréning je zdarma, aby ste si capoeiru vedeli nezáväzne vyskúšať.",
          },
          {
            question: "Potrebujem mať kondíciu alebo skúsenosti?",
            answer:
              "Nie. Nepotrebuješ byť ohybný ani mať kondíciu. Všetko ťa naučíme od nuly, stačí mať chuť cvičiť.",
          },
          {
            question: "Čo si mám obliecť na prvý tréning?",
            answer:
              "Stačí pohodlné športové oblečenie, ideálne biele tričko a tepláky. Trénujeme naboso.",
          },
        ]}
      />
    </div>
  );
}

export default KontaktPage;
